/**
 * PasswordChangeModal — Phase 2 §2.9.
 *
 * Non-dismissible modal. Triggered when the backend signals
 * `must_change_password=true` on /api/auth/login or /api/auth/me, OR when
 * any other endpoint returns 403 with `error: "password_change_required"`.
 *
 * Theme: rouge / noir / or premium — matches the rest of the dashboard.
 */
import React, { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";

const POLICY = {
  minLen: 10,
  rules: [
    { id: "len", label: "Au moins 10 caractères", check: (s) => s.length >= 10 },
    { id: "upper", label: "Une lettre majuscule", check: (s) => /[A-Z]/.test(s) },
    { id: "lower", label: "Une lettre minuscule", check: (s) => /[a-z]/.test(s) },
    { id: "digit", label: "Un chiffre", check: (s) => /\d/.test(s) },
    { id: "special", label: "Un caractère spécial (! @ # $ etc.)", check: (s) => /[^A-Za-z0-9]/.test(s) },
  ],
};

const PIN_POLICY = {
  rules: [
    { id: "len", label: "Au moins 6 chiffres", check: (s) => /^\d{6,}$/.test(s) },
  ],
};

export default function PasswordChangeModal({ isClient = false, onSuccess, reason }) {
  const [oldVal, setOldVal] = useState("");
  const [newVal, setNewVal] = useState("");
  const [confirmVal, setConfirmVal] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const policy = isClient ? PIN_POLICY : POLICY;
  const passed = useMemo(() => policy.rules.map((r) => ({ ...r, ok: r.check(newVal) })), [policy.rules, newVal]);
  const allOk = passed.every((r) => r.ok) && newVal === confirmVal && oldVal.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allOk || submitting) return;
    setSubmitting(true);
    try {
      const url = isClient ? "/api/client/change-pin" : "/api/auth/change-password";
      const payload = isClient
        ? { old_pin: oldVal, new_pin: newVal }
        : { old_password: oldVal, new_password: newVal };
      const token = localStorage.getItem("token");
      await axios.post(`${process.env.REACT_APP_BACKEND_URL || ""}${url}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(isClient ? "PIN mis à jour avec succès" : "Mot de passe mis à jour avec succès");
      onSuccess && onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de la mise à jour.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const subject = isClient ? "PIN" : "mot de passe";

  return (
    <div
      data-testid="password-change-modal"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pwd-change-title"
      onKeyDown={(e) => e.key === "Escape" && e.preventDefault()}
    >
      <div className="w-full max-w-md mx-4 bg-[#0a0a0a] border-2 border-[#dc2626]/60 rounded-2xl shadow-2xl shadow-[#dc2626]/20 overflow-hidden">
        <div className="bg-gradient-to-r from-[#dc2626] via-[#7f1d1d] to-[#0a0a0a] px-6 py-4 border-b border-[#fbbf24]/30">
          <h2 id="pwd-change-title" className="text-xl font-bold text-[#fbbf24] flex items-center gap-2">
            <span aria-hidden>🔒</span> Mise à jour de sécurité
          </h2>
        </div>

        <div className="px-6 py-5 space-y-4 text-[#f5f5f5]">
          <p className="text-sm leading-relaxed">
            Suite à une mise à jour de sécurité de notre plateforme (avril 2026), nous vous
            invitons à définir un nouveau {subject}. Votre sécurité est notre priorité.
            <span className="block text-xs text-[#fbbf24]/80 mt-1">Cette opération est à faire une seule fois.</span>
          </p>

          <form onSubmit={handleSubmit} className="space-y-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[#9ca3af]">Ancien {subject}</span>
              <input
                data-testid="pwd-old-input"
                type={showPwd ? "text" : "password"}
                inputMode={isClient ? "numeric" : "text"}
                value={oldVal}
                onChange={(e) => setOldVal(e.target.value)}
                className="mt-1 w-full bg-[#111] border border-[#2a2a2a] focus:border-[#fbbf24] focus:outline-none rounded-lg px-3 py-2 text-sm"
                autoComplete="current-password"
                required
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[#9ca3af]">Nouveau {subject}</span>
              <input
                data-testid="pwd-new-input"
                type={showPwd ? "text" : "password"}
                inputMode={isClient ? "numeric" : "text"}
                value={newVal}
                onChange={(e) => setNewVal(e.target.value)}
                className="mt-1 w-full bg-[#111] border border-[#2a2a2a] focus:border-[#fbbf24] focus:outline-none rounded-lg px-3 py-2 text-sm"
                autoComplete="new-password"
                required
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-[#9ca3af]">Confirmer le nouveau {subject}</span>
              <input
                data-testid="pwd-confirm-input"
                type={showPwd ? "text" : "password"}
                inputMode={isClient ? "numeric" : "text"}
                value={confirmVal}
                onChange={(e) => setConfirmVal(e.target.value)}
                className="mt-1 w-full bg-[#111] border border-[#2a2a2a] focus:border-[#fbbf24] focus:outline-none rounded-lg px-3 py-2 text-sm"
                autoComplete="new-password"
                required
              />
            </label>

            <button
              type="button"
              data-testid="pwd-show-toggle"
              onClick={() => setShowPwd((v) => !v)}
              className="text-xs text-[#fbbf24] hover:underline"
            >
              {showPwd ? "Masquer" : "Afficher"} le {subject}
            </button>

            <ul className="text-xs space-y-1 mt-1 border-t border-[#2a2a2a] pt-3">
              {passed.map((r) => (
                <li key={r.id} className="flex items-center gap-2">
                  <span className={r.ok ? "text-[#fbbf24]" : "text-[#6b7280]"} aria-hidden>
                    {r.ok ? "✓" : "○"}
                  </span>
                  <span className={r.ok ? "text-[#f5f5f5]" : "text-[#6b7280]"}>{r.label}</span>
                </li>
              ))}
              {newVal && confirmVal && newVal !== confirmVal && (
                <li className="text-red-400 mt-1">Les deux saisies ne correspondent pas.</li>
              )}
            </ul>

            <button
              type="submit"
              data-testid="pwd-submit-btn"
              disabled={!allOk || submitting}
              className="w-full mt-2 py-2.5 rounded-lg bg-[#fbbf24] hover:bg-[#f59e0b] disabled:bg-[#3a3a3a] disabled:text-[#6b7280] text-[#0a0a0a] font-semibold tracking-wide transition-colors"
            >
              {submitting ? "Mise à jour en cours..." : "Valider"}
            </button>

            <a
              href="/forgot-password"
              data-testid="pwd-forgot-link"
              className="block text-center text-xs text-[#9ca3af] hover:text-[#fbbf24] underline mt-2"
            >
              J'ai oublié mon {subject}
            </a>
          </form>
        </div>

        {reason && (
          <div className="px-6 py-2 text-[10px] text-[#6b7280] border-t border-[#2a2a2a] bg-[#050505]">
            ref: {reason}
          </div>
        )}
      </div>
    </div>
  );
}
